/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/**
 * Globally place available collection products before sold-out products.
 *
 * Shopify paginates collection.products before Liquid renders it. A dedicated
 * read-only section returns the complete filtered collection in Shopify's
 * active native order. The browser validates that snapshot, stably partitions
 * it by product.available, and only then slices the requested storefront page.
 * Shopify's two date sorts intentionally bypass this behavior.
 */
(function () {
  'use strict';

  var DATE_SORTS = {
    'created-ascending': true,
    'created-descending': true
  };
  var FEED_SECTION_ID = 'collection-availability-feed';
  var FEED_PAGE_SIZE = 250;
  var FEED_CONCURRENCY = 4;
  var runNumber = 0;
  var activeController = null;
  var scheduleTimer = null;

  function toPositiveInteger(value) {
    var parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  function productCards(container) {
    return Array.prototype.filter.call(container.children, function (child) {
      return child.classList && child.classList.contains('product-item');
    });
  }

  function scheduleAvailabilitySort(delay) {
    if (scheduleTimer !== null) {
      return;
    }

    scheduleTimer = window.setTimeout(function () {
      scheduleTimer = null;
      sortCollectionByAvailability();
    }, delay || 0);
  }

  function sortKeyFor(list) {
    return [
      window.location.pathname,
      window.location.search,
      list.dataset.availabilitySortBy,
      list.dataset.availabilitySortPage,
      list.dataset.availabilitySortPages,
      list.dataset.availabilitySortPageSize,
      list.dataset.availabilitySortTotal
    ].join('|');
  }

  function validateFeed(feed, expected) {
    if (!feed) {
      throw new Error('Availability feed is missing.');
    }

    var actual = {
      sortBy: feed.dataset.availabilityFeedSortBy,
      page: toPositiveInteger(feed.dataset.availabilityFeedPage),
      pages: toPositiveInteger(feed.dataset.availabilityFeedPages),
      pageSize: toPositiveInteger(feed.dataset.availabilityFeedPageSize),
      total: toPositiveInteger(feed.dataset.availabilityFeedTotal),
      sectionId: feed.dataset.availabilityFeedSectionId
    };

    if (
      actual.sortBy !== expected.sortBy ||
      actual.page !== expected.page ||
      actual.pages !== expected.pages ||
      actual.pageSize !== FEED_PAGE_SIZE ||
      actual.total !== expected.total ||
      !actual.sectionId
    ) {
      throw new Error('Availability feed metadata is inconsistent.');
    }

    var cards = productCards(feed).map(function (card) {
      return card.cloneNode(true);
    });
    var expectedCount = Math.min(FEED_PAGE_SIZE, expected.total - ((expected.page - 1) * FEED_PAGE_SIZE));

    if (cards.length !== expectedCount) {
      throw new Error('Availability feed page has an unexpected product count.');
    }

    return {
      cards: cards,
      sectionId: actual.sectionId
    };
  }

  async function loadFeedPage(baseUrl, expected, signal, snapshotToken) {
    var requestUrl = new URL(baseUrl);
    requestUrl.searchParams.set('page', String(expected.page));
    requestUrl.searchParams.set('section_id', FEED_SECTION_ID);
    requestUrl.searchParams.set('_availability_sort_snapshot', snapshotToken);

    var response = await window.fetch(requestUrl.toString(), {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      signal: signal
    });

    if (!response.ok) {
      throw new Error('Availability feed request failed with status ' + response.status + '.');
    }

    var html = await response.text();
    var parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    return validateFeed(parsedDocument.querySelector('.availability-sort-feed'), expected);
  }

  async function loadFeedPages(baseUrl, sortBy, total, signal, snapshotToken) {
    var feedPageCount = Math.ceil(total / FEED_PAGE_SIZE);
    var results = new Array(feedPageCount);
    var nextPage = 1;

    async function worker() {
      while (nextPage <= feedPageCount) {
        var page = nextPage;
        nextPage += 1;
        results[page - 1] = await loadFeedPage(baseUrl, {
          sortBy: sortBy,
          page: page,
          pages: feedPageCount,
          total: total
        }, signal, snapshotToken);
      }
    }

    var workers = [];
    var workerCount = Math.min(FEED_CONCURRENCY, feedPageCount);

    for (var index = 0; index < workerCount; index += 1) {
      workers.push(worker());
    }

    await Promise.all(workers);
    return results;
  }

  function retargetSectionReferences(card, sourceSectionId, targetSectionId) {
    if (sourceSectionId === targetSectionId) {
      return;
    }

    var attributes = [
      'id',
      'for',
      'name',
      'form',
      'aria-controls',
      'aria-labelledby',
      'aria-describedby'
    ];
    var elements = [card].concat(Array.prototype.slice.call(card.querySelectorAll('*')));

    elements.forEach(function (element) {
      attributes.forEach(function (attribute) {
        var value = element.getAttribute(attribute);

        if (value && value.indexOf(sourceSectionId) !== -1) {
          element.setAttribute(attribute, value.split(sourceSectionId).join(targetSectionId));
        }
      });
    });
  }

  async function sortCollectionByAvailability() {
    var section = document.querySelector('[data-section-type="collection"]');
    var list = section && section.querySelector('.product-list--collection[data-availability-sort="global"]');

    if (!section || !list) {
      return;
    }

    var sortBy = list.dataset.availabilitySortBy;
    var currentPage = toPositiveInteger(list.dataset.availabilitySortPage);
    var pageCount = toPositiveInteger(list.dataset.availabilitySortPages);
    var pageSize = toPositiveInteger(list.dataset.availabilitySortPageSize);
    var total = toPositiveInteger(list.dataset.availabilitySortTotal);
    var sectionId = section.dataset.sectionId;
    var operationKey = sortKeyFor(list);

    if (
      !sortBy ||
      !currentPage ||
      !pageCount ||
      !pageSize ||
      !total ||
      !sectionId ||
      currentPage > pageCount
    ) {
      list.dataset.availabilitySortStatus = 'invalid';
      return;
    }

    if (list.dataset.availabilitySortKey === operationKey) {
      return;
    }

    list.dataset.availabilitySortKey = operationKey;

    if (DATE_SORTS[sortBy]) {
      list.dataset.availabilitySortStatus = 'native-date-order';
      return;
    }

    var attempt = toPositiveInteger(list.dataset.availabilitySortAttempt) || 0;
    list.dataset.availabilitySortAttempt = String(attempt + 1);
    list.dataset.availabilitySortStatus = 'loading';
    list.setAttribute('aria-busy', 'true');

    runNumber += 1;
    var thisRun = runNumber;

    if (activeController) {
      activeController.abort();
    }

    activeController = new AbortController();
    var signal = activeController.signal;
    var baseUrl = new URL(window.location.href);
    baseUrl.searchParams.delete('section_id');
    baseUrl.searchParams.delete('_availability_sort_snapshot');
    var snapshotToken = String(Date.now()) + '-' + String(thisRun);

    try {
      var feedPages = await loadFeedPages(baseUrl, sortBy, total, signal, snapshotToken);

      if (
        thisRun !== runNumber ||
        signal.aborted ||
        !document.documentElement.contains(list)
      ) {
        return;
      }

      var sourceSectionId = feedPages[0].sectionId;
      var allCards = [];

      feedPages.forEach(function (feedPage) {
        if (feedPage.sectionId !== sourceSectionId) {
          throw new Error('Availability feed section identity changed.');
        }

        allCards = allCards.concat(feedPage.cards);
      });

      if (allCards.length !== total) {
        throw new Error('Availability feed total does not match the collection.');
      }

      var handles = new Set();
      var available = [];
      var soldOut = [];

      allCards.forEach(function (card) {
        var handle = card.dataset.productHandle;
        var availability = card.dataset.productAvailable;

        if (!handle || handles.has(handle) || (availability !== 'true' && availability !== 'false')) {
          throw new Error('Collection product identity or availability is invalid.');
        }

        handles.add(handle);

        if (availability === 'true') {
          available.push(card);
        } else {
          soldOut.push(card);
        }
      });

      if (handles.size !== total) {
        throw new Error('Collection contains duplicate or missing products.');
      }

      var globallySorted = available.concat(soldOut);
      var firstIndex = (currentPage - 1) * pageSize;
      var pageCards = globallySorted.slice(firstIndex, firstIndex + pageSize);
      var expectedPageCount = Math.min(pageSize, total - firstIndex);

      if (pageCards.length !== expectedPageCount) {
        throw new Error('Sorted collection page has an unexpected size.');
      }

      var fragment = document.createDocumentFragment();
      pageCards.forEach(function (card) {
        retargetSectionReferences(card, sourceSectionId, sectionId);
        fragment.appendChild(card);
      });

      list.replaceChildren(fragment);
      list.dataset.availabilitySortStatus = 'ready';
      list.dataset.availabilitySortAvailableCount = String(available.length);
      list.dataset.availabilitySortSoldOutCount = String(soldOut.length);
      list.removeAttribute('aria-busy');

      document.documentElement.dispatchEvent(new CustomEvent('collection:availability-sorted', {
        bubbles: true,
        detail: {
          available: available.length,
          soldOut: soldOut.length,
          page: currentPage,
          pages: pageCount,
          sortBy: sortBy
        }
      }));
    } catch (error) {
      if (thisRun !== runNumber || signal.aborted) {
        return;
      }

      list.removeAttribute('aria-busy');

      if (attempt < 1) {
        list.removeAttribute('data-availability-sort-key');
        list.dataset.availabilitySortStatus = 'retrying';
        scheduleAvailabilitySort(1000);
      } else {
        list.dataset.availabilitySortStatus = 'failed-open';
        console.warn('[Mission Gear Pro] Global availability sorting was skipped:', error);
      }
    }
  }

  function startAvailabilitySorting() {
    scheduleAvailabilitySort(0);

    var observer = new MutationObserver(function (mutations) {
      var collectionChanged = mutations.some(function (mutation) {
        var target = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;

        if (
          target &&
          target.closest &&
          target.closest('[data-section-type="collection"]')
        ) {
          return true;
        }

        return Array.prototype.some.call(mutation.addedNodes, function (node) {
          return node.nodeType === 1 && (
            (node.matches && node.matches('[data-section-type="collection"], .product-list--collection')) ||
            (node.querySelector && node.querySelector('[data-section-type="collection"], .product-list--collection'))
          );
        });
      });

      if (collectionChanged) {
        scheduleAvailabilitySort(50);
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startAvailabilitySorting, { once: true });
  } else {
    startAvailabilitySorting();
  }
}());

/**
 * Turn the curated homepage brand logos into a continuous mobile carousel.
 * A single aria-hidden copy creates a seamless loop. Manual swiping remains
 * available and motion pauses while the visitor interacts with the track.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '.template-index main > [id$="__logo_list_r88gb8"]';
  var MOBILE_QUERY = '(max-width: 999px)';
  var SPEED_PX_PER_SECOND = 50;
  var RESUME_DELAY = 1200;

  function initializeBrandCarousel(section) {
    var track = section && section.querySelector('.quick-links');

    if (!track || track.dataset.mgpBrandCarouselInitialized === 'true') {
      return;
    }

    var originals = Array.prototype.slice.call(
      track.querySelectorAll('.quick-links__link')
    );

    if (originals.length < 2) {
      return;
    }

    var previousButton = section.querySelector('[data-mgp-brand-prev]');
    var nextButton = section.querySelector('[data-mgp-brand-next]');
    var mobileViewport = window.matchMedia(MOBILE_QUERY);
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var clones = [];
    var animationFrame = null;
    var resumeTimer = null;
    var lastFrameTime = null;
    var loopWidth = 0;
    var scrollPosition = 0;
    var appliedScrollPosition = 0;
    var pointerActive = false;
    var focused = false;
    var inView = true;

    track.dataset.mgpBrandCarouselInitialized = 'true';
    track.classList.add('mgp-brand-carousel');

    function updateNavigation() {
      if (!previousButton || !nextButton) return;
      var maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      var show = !mobileViewport.matches && maximum > 2;
      previousButton.hidden = !show;
      nextButton.hidden = !show;
      previousButton.disabled = !show;
      nextButton.disabled = !show;
    }

    function scrollPage(direction) {
      var cellWidth = originals[0].getBoundingClientRect().width;
      var cellsPerPage = Math.max(1, Math.round(track.clientWidth / cellWidth));
      var maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      var next = Math.max(0, Math.min(maximum, track.scrollLeft + direction * cellWidth * cellsPerPage));
      if (direction < 0 && track.scrollLeft <= 2) next = maximum;
      if (direction > 0 && track.scrollLeft >= maximum - 2) next = 0;
      track.scrollTo({ left: next, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    if (previousButton && nextButton) {
      previousButton.addEventListener('click', function () { scrollPage(-1); });
      nextButton.addEventListener('click', function () { scrollPage(1); });
      track.addEventListener('scroll', updateNavigation, { passive: true });
    }

    function removeDuplicateIds(root) {
      if (root.id) {
        root.removeAttribute('id');
      }

      Array.prototype.forEach.call(root.querySelectorAll('[id]'), function (element) {
        element.removeAttribute('id');
      });
    }

    function addClones() {
      if (clones.length) {
        return;
      }

      clones = originals.map(function (original) {
        var clone = original.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        clone.dataset.mgpBrandCarouselClone = 'true';
        removeDuplicateIds(clone);

        Array.prototype.forEach.call(
          clone.querySelectorAll('a, button, input, select, textarea, [tabindex]'),
          function (element) {
            element.setAttribute('tabindex', '-1');
          }
        );

        Array.prototype.forEach.call(clone.querySelectorAll('img'), function (image) {
          image.alt = '';
        });

        track.appendChild(clone);
        return clone;
      });
    }

    function removeClones() {
      clones.forEach(function (clone) {
        clone.remove();
      });

      clones = [];
      loopWidth = 0;
    }

    function measureLoop() {
      if (!clones.length) {
        loopWidth = 0;
        return;
      }

      loopWidth = clones[0].offsetLeft - originals[0].offsetLeft;
    }

    function normalizePosition() {
      if (loopWidth <= 0) {
        return;
      }

      scrollPosition = ((track.scrollLeft % loopWidth) + loopWidth) % loopWidth;
      track.scrollLeft = scrollPosition;
      appliedScrollPosition = track.scrollLeft;
    }

    function canAutoplay() {
      return (
        mobileViewport.matches &&
        !reducedMotion.matches &&
        !pointerActive &&
        !focused &&
        !document.hidden &&
        section.isConnected &&
        inView
      );
    }

    function stopAnimation() {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }

      animationFrame = null;
      lastFrameTime = null;
    }

    function animate(timestamp) {
      if (!canAutoplay()) {
        stopAnimation();
        return;
      }

      if (lastFrameTime === null) {
        lastFrameTime = timestamp;
      }

      var elapsed = Math.min(64, timestamp - lastFrameTime);
      lastFrameTime = timestamp;
      if (Math.abs(track.scrollLeft - appliedScrollPosition) > 1) {
        scrollPosition = track.scrollLeft;
      }
      scrollPosition = (scrollPosition + SPEED_PX_PER_SECOND * (elapsed / 1000)) % loopWidth;
      track.scrollLeft = scrollPosition;
      appliedScrollPosition = track.scrollLeft;
      animationFrame = window.requestAnimationFrame(animate);
    }

    function startAnimation() {
      window.clearTimeout(resumeTimer);
      resumeTimer = null;

      if (!canAutoplay() || animationFrame !== null) {
        return;
      }

      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';
      addClones();
      measureLoop();
      normalizePosition();

      if (loopWidth > 0) {
        track.dataset.mgpBrandCarouselMode = 'continuous';
        animationFrame = window.requestAnimationFrame(animate);
      }
    }

    function resumeLater() {
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(startAnimation, RESUME_DELAY);
    }

    function disableContinuousMode() {
      window.clearTimeout(resumeTimer);
      resumeTimer = null;
      stopAnimation();
      track.removeAttribute('data-mgp-brand-carousel-mode');
      track.style.removeProperty('scroll-snap-type');
      track.style.removeProperty('scroll-behavior');
      track.scrollLeft = 0;
      removeClones();
    }

    function updateMode() {
      if (!mobileViewport.matches || reducedMotion.matches) {
        disableContinuousMode();
        updateNavigation();
        return;
      }

      addClones();
      measureLoop();
      startAnimation();
      updateNavigation();
    }

    track.addEventListener('pointerdown', function () {
      pointerActive = true;
      focused = false;
      stopAnimation();
    });

    function finishPointerInteraction() {
      if (!pointerActive) return;
      pointerActive = false;
      normalizePosition();
      resumeLater();
    }

    window.addEventListener('pointerup', finishPointerInteraction);
    window.addEventListener('pointercancel', finishPointerInteraction);
    track.addEventListener('wheel', function () {
      stopAnimation();
      resumeLater();
    }, { passive: true });

    track.addEventListener('focusin', function () {
      focused = Boolean(document.activeElement && document.activeElement.matches(':focus-visible'));
      if (focused) stopAnimation();
    });

    track.addEventListener('focusout', function () {
      focused = false;
      resumeLater();
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stopAnimation();
      } else {
        resumeLater();
      }
    });

    window.addEventListener('pageshow', function () {
      pointerActive = false;
      focused = Boolean(track.contains(document.activeElement) && document.activeElement.matches(':focus-visible'));
      resumeLater();
    });

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        inView = entries[0] ? entries[0].isIntersecting : true;

        if (inView) {
          startAnimation();
        } else {
          stopAnimation();
        }
      }, {
        rootMargin: '120px 0px'
      });

      observer.observe(section);
    }

    function handleViewportChange() {
      updateMode();
    }

    if (mobileViewport.addEventListener) {
      mobileViewport.addEventListener('change', handleViewportChange);
      reducedMotion.addEventListener('change', handleViewportChange);
    } else {
      mobileViewport.addListener(handleViewportChange);
      reducedMotion.addListener(handleViewportChange);
    }

    window.addEventListener('resize', function () {
      updateNavigation();
      if (!mobileViewport.matches) {
        return;
      }

      measureLoop();
      normalizePosition();
    });

    updateMode();
  }

  function startBrandCarousel(root) {
    var scope = root || document;
    var section = scope.matches && scope.matches(SECTION_SELECTOR)
      ? scope
      : scope.querySelector && scope.querySelector(SECTION_SELECTOR);

    if (section) {
      initializeBrandCarousel(section);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      startBrandCarousel(document);
    }, { once: true });
  } else {
    startBrandCarousel(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    startBrandCarousel(event.target);
  });
}());

(function () {
  'use strict';

  var SECTION_SELECTOR = '.template-index main > [id$="__logo_list_r88gb8"]';
  var DESKTOP_INTERVAL = 5000;
  var controllers = new Map();

  function initializeDesktopCarousel(section) {
    if (controllers.has(section)) return;
    var track = section.querySelector('.quick-links');
    var shell = section.querySelector('.mgp-brand-carousel-shell');
    var toggle = section.querySelector('[data-mgp-brand-autoplay]');
    if (!track || !shell || !toggle) return;

    var desktop = window.matchMedia('(min-width: 1000px)');
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var abortController = new AbortController();
    var listenerOptions = { signal: abortController.signal };
    var timer = null;
    var observer = null;
    var inView = !('IntersectionObserver' in window);
    var hovered = shell.matches(':hover');
    var pointerActive = false;
    var paused = false;

    function maximumScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    function stop() {
      window.clearTimeout(timer);
      timer = null;
    }

    function canAdvance() {
      return desktop.matches && !reducedMotion.matches && !paused &&
        !hovered && !pointerActive && !(shell.contains(document.activeElement) && document.activeElement.matches(':focus-visible')) &&
        !document.hidden && inView && section.isConnected && maximumScroll() > 2;
    }

    function schedule() {
      stop();
      if (canAdvance()) timer = window.setTimeout(advance, DESKTOP_INTERVAL);
    }

    function advance() {
      timer = null;
      if (!canAdvance()) return;
      var first = track.querySelector('.quick-links__link');
      var cellWidth = first && first.getBoundingClientRect().width;
      if (!cellWidth) return;
      var pageWidth = cellWidth * Math.max(1, Math.round(track.clientWidth / cellWidth));
      var maximum = maximumScroll();
      var next = track.scrollLeft >= maximum - 2 ? 0 : Math.min(maximum, track.scrollLeft + pageWidth);
      track.scrollTo({ left: next, behavior: 'smooth' });
      schedule();
    }

    function update() {
      toggle.hidden = !desktop.matches || reducedMotion.matches || maximumScroll() <= 2;
      var label = paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel;
      toggle.setAttribute('aria-label', label);
      toggle.setAttribute('title', label);
      toggle.querySelector('[data-mgp-brand-pause-icon]').toggleAttribute('hidden', paused);
      toggle.querySelector('[data-mgp-brand-play-icon]').toggleAttribute('hidden', !paused);
      schedule();
    }

    toggle.addEventListener('click', function () {
      paused = !paused;
      update();
    }, listenerOptions);
    shell.addEventListener('pointerenter', function (event) {
      if (event.pointerType === 'mouse' || event.pointerType === 'pen') {
        hovered = true;
        stop();
      }
    }, listenerOptions);
    shell.addEventListener('pointerleave', function () {
      hovered = false;
      schedule();
    }, listenerOptions);
    shell.addEventListener('pointerdown', function () {
      pointerActive = true;
      stop();
    }, listenerOptions);
    window.addEventListener('pointerup', function () {
      if (!pointerActive) return;
      pointerActive = false;
      schedule();
    }, listenerOptions);
    window.addEventListener('pointercancel', function () {
      pointerActive = false;
      schedule();
    }, listenerOptions);
    shell.addEventListener('focusin', stop, listenerOptions);
    shell.addEventListener('focusout', function () {
      queueMicrotask(schedule);
    }, listenerOptions);
    shell.addEventListener('click', schedule, listenerOptions);
    shell.addEventListener('wheel', schedule, { passive: true, signal: abortController.signal });
    document.addEventListener('visibilitychange', schedule, listenerOptions);
    window.addEventListener('resize', update, listenerOptions);
    desktop.addEventListener('change', function () {
      hovered = shell.matches(':hover');
      pointerActive = false;
      update();
    }, listenerOptions);
    reducedMotion.addEventListener('change', update, listenerOptions);

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        inView = entries[0] ? entries[0].isIntersecting : false;
        schedule();
      });
      observer.observe(track);
    }

    controllers.set(section, function () {
      stop();
      abortController.abort();
      if (observer) observer.disconnect();
      controllers.delete(section);
    });
    update();
  }

  function start(root) {
    var section = root.matches && root.matches(SECTION_SELECTOR)
      ? root : root.querySelector && root.querySelector(SECTION_SELECTOR);
    if (section) initializeDesktopCarousel(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { start(document); }, { once: true });
  } else {
    start(document);
  }
  document.addEventListener('shopify:section:load', function (event) { start(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    var dispose = controllers.get(event.target);
    if (dispose) dispose();
  });
}());


(function () {
  'use strict';

  document.addEventListener('variant:change', function (event) {
    var variant = event.detail && event.detail.variant;
    var section = event.target.closest && event.target.closest('[data-section-type]');
    var gallery = section && section.querySelector('[data-mgp-gallery-options]');
    if (!variant || !gallery) return;

    var options;
    try {
      options = JSON.parse(gallery.getAttribute('data-mgp-gallery-options'));
    } catch (error) {
      return;
    }

    var cells = Array.prototype.slice.call(gallery.querySelectorAll('.product-gallery__carousel-item'));
    if (!cells.length) return;

    var requireColorMatch = gallery.getAttribute('data-mgp-require-color-match') === 'true';
    var hasMatchingMedia = cells.some(function (cell) {
      if (requireColorMatch && variant.featured_media && String(variant.featured_media.id) === cell.getAttribute('data-media-id')) return true;
      var group = cell.getAttribute('data-mgp-group-name');
      if (!group) return !requireColorMatch;
      var optionIndex = options.findIndex(function (option) {
        return option.toLowerCase() === group;
      });
      if (optionIndex < 0) return !requireColorMatch;
      var value = String(variant['option' + (optionIndex + 1)] || '').toLowerCase();
      return value === cell.getAttribute('data-mgp-group-value') ||
        Boolean(variant.featured_media && String(variant.featured_media.id) === cell.getAttribute('data-media-id'));
    });

    var fallback = !hasMatchingMedia;
    var referenceId = gallery.getAttribute('data-mgp-reference-media-id');
    var first = cells.find(function (cell) {
      return cell.getAttribute('data-media-id') === referenceId;
    });
    var thumbnail = gallery.querySelector('.product-gallery__thumbnail[data-media-id="' + referenceId + '"]');
    [first, thumbnail].filter(Boolean).forEach(function (element) {
      var group = element.getAttribute('data-mgp-group-name');
      if (fallback || cells.length === 1 || !group) {
        element.removeAttribute('data-group-name');
        element.removeAttribute('data-group-value');
        element.classList.remove('is-filtered');
      } else {
        element.setAttribute('data-group-name', group);
        element.setAttribute('data-group-value', element.getAttribute('data-mgp-group-value'));
      }
    });

    var note = gallery.querySelector('[data-gallery-fallback-note]');
    if (note) note.hidden = !fallback;
    gallery.setAttribute('data-mgp-gallery-fallback', String(fallback));

    // Warehouse rebuilds its carousel in the next animation frame. Its old
    // selection can fall outside the smaller gallery, leaving no active image.
    if (cells.length > 1) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (!gallery.isConnected) return;
          // Missing L4 colour photos use the Multicam reference in the gallery only.
          // Do not attach that reference to the selected Shopify variant.
          if (fallback && requireColorMatch) {
            var referenceThumbnail = gallery.querySelector('.product-gallery__thumbnail[data-media-id="' + referenceId + '"]');
            if (referenceThumbnail) referenceThumbnail.click();
            return;
          }
          if (gallery.querySelector('.product-gallery__carousel-item.is-selected:not(.is-filtered)')) return;
          var visibleThumbnail = gallery.querySelector('.product-gallery__thumbnail:not(.is-filtered)');
          if (visibleThumbnail) visibleThumbnail.click();
        });
      });
    }
  }, true);
}());
(function () {
  function initializeHomepageCarouselLabels() {
    var root = document.querySelector('.template-index #main');
    var labels = window.languages;
    if (!root || !labels || !labels.carouselPrevious || !labels.carouselNext) return;
    function labelButtons(scope) {
      if (scope.nodeType !== 1) return;
      var buttons = [];
      if (scope.matches('.flickity-prev-next-button')) buttons.push(scope);
      Array.prototype.push.apply(buttons, scope.querySelectorAll('.flickity-prev-next-button'));
      buttons.forEach(function (button) {
        button.setAttribute('aria-label', button.classList.contains('previous') ? labels.carouselPrevious : labels.carouselNext);
      });
    }
    labelButtons(root);
    new MutationObserver(function (records) {
      records.forEach(function (record) {
        record.addedNodes.forEach(labelButtons);
      });
    }).observe(root, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeHomepageCarouselLabels, { once: true });
  } else {
    initializeHomepageCarouselLabels();
  }
}());



(function () {
  document.addEventListener('change', function (event) {
    var radio = event.target.closest('.product-item .color-swatch__radio');
    if (!radio) return;
    var card = radio.closest('.product-item');
    var image = card.querySelector('.product-item__primary-image');
    if (!image) return;
    if (radio.hasAttribute('data-image-url')) {
      image.alt = radio.getAttribute('data-mgp-image-alt') || (card.querySelector('.product-item__title').textContent.trim() + ' – ' + radio.value);
      var ratio = Number(radio.getAttribute('data-image-aspect-ratio'));
      if (ratio > 0) image.closest('.aspect-ratio').style.paddingBottom = (100 / ratio) + '%';
    }
    if (!radio.hasAttribute('data-secondary-image-url')) return;
    var wrapper = image.closest('.product-item__image-wrapper');
    var secondaryUrl = radio.getAttribute('data-secondary-image-url');
    var secondary = card.querySelector('.product-item__secondary-image');
    if (secondaryUrl) {
      if (!secondary) {
        secondary = image.cloneNode(false);
        secondary.classList.remove('product-item__primary-image');
        secondary.classList.add('product-item__secondary-image');
        image.parentElement.appendChild(secondary);
      }
      secondary.removeAttribute('srcset');
      secondary.src = secondaryUrl;
      secondary.alt = image.alt;
      secondary.setAttribute('data-media-id', radio.getAttribute('data-secondary-media-id'));
    }
    if (wrapper) wrapper.classList.toggle('product-item__image-wrapper--with-secondary', !!secondaryUrl);
  });
})();
